// SPDX-License-Identifier: AGPL-3.0-or-later

import type {DesktopGameOverlayCorner} from '@app/types/electron.d';

export type GameOverlayMode = 'speaking' | 'room';

interface GameOverlayEntryLike {
	userId: string;
	speaking: boolean;
}

export interface GameOverlaySelection<T> {
	shown: Array<T>;
	overflow: number;
}

export type GameOverlayMicState = 'on' | 'muted' | 'deafened';

export interface GameOverlaySize {
	width: number;
	height: number;
}

export interface GameOverlayPoint {
	x: number;
	y: number;
}

export interface GameOverlayAnchor {
	corner: DesktopGameOverlayCorner;
	offsetX: number;
	offsetY: number;
}

export const GAME_OVERLAY_SCALE_MIN = 75;
export const GAME_OVERLAY_SCALE_MAX = 200;
// Shells before 1.0.12 cap the window at 720 px, which fits the fullest room overlay at 150%.
export const GAME_OVERLAY_SCALE_MAX_WITHOUT_PLACEMENT = 150;

const MAX_SHOWN: Record<GameOverlayMode, number> = {speaking: 6, room: 15};

// Keep in sync with GameOverlayHost.module.css.
const PADDING_PX = 6;
const SPEAKING_AVATAR_PX = 28;
const SPEAKING_GAP_PX = 6;
const SPEAKING_WIDTH_PX = SPEAKING_AVATAR_PX + 2 * PADDING_PX;
const ROOM_ROW_PX = 28;
const ROOM_WIDTH_PX = 154;
// Same as GAME_OVERLAY_MARGIN in fluxer_desktop GameOverlayLayout.ts.
const EDGE_MARGIN_PX = 16;

export function selectGameOverlayEntries<T extends GameOverlayEntryLike>(
	entries: ReadonlyArray<T>,
	mode: GameOverlayMode,
): GameOverlaySelection<T> {
	const byUser = new Map<string, T>();
	for (const entry of entries) {
		// Same rule as deduplicateEntriesByUser in VoiceParticipantAvatarList.tsx: the speaking connection wins whole.
		const existing = byUser.get(entry.userId);
		if (!existing || (entry.speaking && !existing.speaking)) {
			byUser.set(entry.userId, entry);
		}
	}
	const candidates = Array.from(byUser.values()).filter((entry) => mode === 'room' || entry.speaking);
	const limit = MAX_SHOWN[mode];
	return {shown: candidates.slice(0, limit), overflow: Math.max(0, candidates.length - limit)};
}

export function clampGameOverlayScale(scale: number, max: number): number {
	return Math.min(max, Math.max(GAME_OVERLAY_SCALE_MIN, Math.round(scale)));
}

export function computeGameOverlaySize(
	mode: GameOverlayMode,
	shownCount: number,
	overflow: number,
	scale = 100,
): GameOverlaySize {
	const rows = shownCount + (overflow > 0 ? 1 : 0);
	const size =
		mode === 'speaking'
			? {
					width: SPEAKING_WIDTH_PX,
					height: 2 * PADDING_PX + rows * SPEAKING_AVATAR_PX + Math.max(0, rows - 1) * SPEAKING_GAP_PX,
				}
			: {width: ROOM_WIDTH_PX, height: 2 * PADDING_PX + rows * ROOM_ROW_PX};
	return {width: Math.ceil((size.width * scale) / 100), height: Math.ceil((size.height * scale) / 100)};
}

export function computeGameOverlayPosition(
	{corner, offsetX, offsetY}: GameOverlayAnchor,
	overlay: GameOverlaySize,
	area: GameOverlaySize,
): GameOverlayPoint {
	const roomX = Math.max(0, area.width - 2 * EDGE_MARGIN_PX - overlay.width);
	const roomY = Math.max(0, area.height - 2 * EDGE_MARGIN_PX - overlay.height);
	const insetX = EDGE_MARGIN_PX + Math.min(offsetX, roomX);
	const insetY = EDGE_MARGIN_PX + Math.min(offsetY, roomY);
	return {
		x: corner.endsWith('left') ? insetX : area.width - overlay.width - insetX,
		y: corner.startsWith('top') ? insetY : area.height - overlay.height - insetY,
	};
}

export function anchorGameOverlayAt(
	{x, y}: GameOverlayPoint,
	overlay: GameOverlaySize,
	area: GameOverlaySize,
): GameOverlayAnchor {
	const left = x + overlay.width / 2 < area.width / 2;
	const top = y + overlay.height / 2 < area.height / 2;
	const offsetX = left ? x - EDGE_MARGIN_PX : area.width - EDGE_MARGIN_PX - overlay.width - x;
	const offsetY = top ? y - EDGE_MARGIN_PX : area.height - EDGE_MARGIN_PX - overlay.height - y;
	return {
		corner: `${top ? 'top' : 'bottom'}-${left ? 'left' : 'right'}`,
		offsetX: Math.max(0, Math.round(offsetX)),
		offsetY: Math.max(0, Math.round(offsetY)),
	};
}

export function getGameOverlayMicState(entry: {selfMute: boolean; selfDeaf: boolean}): GameOverlayMicState {
	if (entry.selfDeaf) return 'deafened';
	return entry.selfMute ? 'muted' : 'on';
}
